import React, { useCallback, useEffect, useState } from 'react';
import { Form, Input, InputNumber, Spin, message } from 'antd';
import { CreditCard } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import stripeServices, {
    type StripeAmountDetails,
    type StripeContractAmount,
    type StripePaymentLinkRow,
    type StripePaymentType,
} from '../../../../services/oneButtonServices/stripeServices';
import { buildStripePageInput, type StripePageInput } from '../../../../helpers/stripeOpportunityInput';
import {
    showStripeFinancePartnerSyncModal,
    stripeFinancePartnerSyncMessage,
} from '../../../../helpers/stripeFinancePartnerSyncGate';
import './oneButton-css/StripePaymentModal.css';

const usd = (value: number | null | undefined): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const formatLinkDate = (value: string | null | undefined): string => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}-${dd}-${d.getFullYear()}`;
};

/** First two name tokens on one line; full name is available on hover when truncated. */
const displayCreatedByName = (name: string | null | undefined): { label: string; full: string } => {
    const full = String(name ?? '').trim();
    const parts = full.split(/\s+/).filter(Boolean);
    const label = parts.slice(0, 2).join(' ');
    return { label, full };
};

const STRIPE_COLUMN_KEYS = [
    'payment',
    'details',
    'date',
    'createdBy',
    'product',
    'paymentType',
    'total',
    'paid',
    'balance',
    'status',
] as const;

type StripeColumnKey = (typeof STRIPE_COLUMN_KEYS)[number];

/**
 * Pin width only on the columns you care about.
 * Any column omitted here shares the leftover space equally.
 */
const STRIPE_PINNED_COLUMN_WIDTHS: Partial<Record<StripeColumnKey, string>> = {
    payment: '10%',
    createdBy: '15%',
    paymentType: '15%',
    status: '8%',
    details: '7%',
};

const stripeColumnWidthStyles = (hasPaymentDetails: boolean): Array<{ width: string }> => {
    const keys = STRIPE_COLUMN_KEYS.filter((key) => key !== 'details' || hasPaymentDetails);
    const pinned = keys.map((key) => {
        const raw = STRIPE_PINNED_COLUMN_WIDTHS[key];
        if (!raw) return null;
        const n = Number.parseFloat(raw);
        return Number.isFinite(n) ? n : null;
    });
    const unspecified = pinned.filter((value) => value == null).length;
    const used = pinned.reduce((sum, value) => sum + (value ?? 0), 0);
    const share = unspecified > 0 ? Math.max(0, 100 - used) / unspecified : 0;
    return pinned.map((value) => ({ width: `${value ?? share}%` }));
};

const paymentTypeName = (row: StripePaymentType): string =>
    String(row.payment_Type_Name || row.Payment_Type_Name || '');

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

/** V1 phone mask `(000) 000 0000` (14 chars). */
const formatPhoneMask = (raw: string): string => {
    const digits = digitsOnly(raw).slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length < 4) return `(${digits}`;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

type StatusState = {
    title: string;
    link: string;
    message: string;
    messageLink: string;
    status: '' | 'success' | 'failed';
};

type NewPaymentForm = {
    customerPreference: string | undefined;
    productType: string | undefined;
    PaymentType: string | undefined;
    description: string;
    amount: number | null;
    address: string;
    email: string;
    phoneNumber: string;
};

const mergeContractAmounts = (
    amountDetails: StripeAmountDetails,
    stripeContractAmount: StripeContractAmount[],
    appName: string,
): StripeAmountDetails => {
    const splits = [...(amountDetails.amountSplitUps ?? [])];
    splits.forEach((el) => {
        const match = stripeContractAmount.find((ele) => ele.ProductName === el.product);
        if (match) {
            el.OpportunityContractId = match.OpportunityContractId;
            el.Contract_Amount = match.Contract_Amount;
            el.balanceRemaining = Number(match.Contract_Amount || 0) - Number(el.payments || 0);
        } else {
            el.Contract_Amount = 0;
        }
    });
    let invoices = 0;
    if (appName === 'oneBUTTON') {
        splits.forEach((ele) => {
            invoices += Number(ele.Contract_Amount || 0);
        });
    } else {
        splits.forEach((ele) => {
            invoices += Number(ele.invoices || 0);
        });
    }
    return {
        ...amountDetails,
        amountSplitUps: splits,
        totalAmount: invoices,
    };
};

type StripePaymentPanelProps = {
    open: boolean;
    onClose: () => void;
    opportunityNumericId: string | number | null | undefined;
    salesForceId: string | null | undefined;
};

const StripePaymentPanel: React.FC<StripePaymentPanelProps> = ({
    open,
    onClose,
    opportunityNumericId,
    salesForceId,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const [loading, setLoading] = useState(false);
    const [links, setLinks] = useState<StripePaymentLinkRow[]>([]);
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});
    const [amountDetails, setAmountDetails] = useState<StripeAmountDetails>({});
    const [pageInput, setPageInput] = useState<StripePageInput | null>(null);
    const [newOpen, setNewOpen] = useState(false);
    const [status, setStatus] = useState<StatusState | null>(null);

    const load = useCallback(async () => {
        const numericId = opportunityNumericId;
        if (numericId == null || String(numericId).trim() === '') return;
        setLoading(true);
        // V1 `setStripeInput` always builds the New Payment payload from opportunity info.
        // Contract-amount / payment-link failures must not leave the button disabled.
        let amounts: StripeContractAmount[] = [];
        try {
            amounts = await stripeServices.getStripeContractAmount(numericId);
        } catch {
            amounts = [];
        }
        const input = buildStripePageInput(opportunityDetails, salesForceId, amounts);
        setPageInput(input);
        try {
            const payload = await stripeServices.getStripePaymentsLinks(
                input.contractId,
                input.opportunityId,
                input.salesforce_Opportunity_Id,
                input.appName,
            );
            const merged = mergeContractAmounts(payload.amountDetails, amounts, input.appName);
            const totalEpc = Number(payload.amountDetails?.totalContractAmount || 0);
            input.totalEpc = totalEpc;
            input.totalAmount = Number(merged.totalAmount || 0);
            setPageInput({ ...input });
            setLinks(payload.payment_Link_Details);
            setAmountDetails(merged);
        } catch {
            message.error('Unable to load Stripe payment links.');
            setLinks([]);
            setAmountDetails({});
        } finally {
            setLoading(false);
        }
    }, [opportunityDetails, opportunityNumericId, salesForceId]);

    useEffect(() => {
        void load();
    }, [load]);

    const splits = amountDetails.amountSplitUps ?? [];
    const hasPaymentDetails = links.some((row) => Array.isArray(row.payments) && row.payments.length > 0);
    const tableColSpan = hasPaymentDetails ? 10 : 9;
    const columnWidths = stripeColumnWidthStyles(hasPaymentDetails);
    const modalClass = `obd-sync-modal obd-commerce-hub-modal obd-theme-${theme}`;
    const modalRoot = `obd-sync-modal-root obd-commerce-hub-modal-root obd-theme-${theme}`;

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title={
                <>
                    <CreditCard weight="duotone" aria-hidden />
                    <span>Stripe</span>
                </>
            }
            width="min(96vw, 1280px)"
            centered
            theme={theme}
            className={`${modalClass} obd-commerce-hub-modal--stripe`}
            rootClassName={modalRoot}
            footer={null}
            destroyOnClose
        >
            <div className={`obd-commerce-hub-modal__shell obd-theme-${theme}`}>
                <div className="obd-sync-modal__content-body obd-commerce-hub-modal__body">
                    <div className={`obd-stripe-page obd-theme-${theme}`}>
            {loading ? (
                <div className="obd-stripe-page__loader">
                    <Spin />
                </div>
            ) : null}

            <div className="obd-stripe-grid">
            <div className="obd-stripe-table-wrap">
                <table className={`obd-stripe-table${hasPaymentDetails ? ' obd-stripe-table--with-details' : ''}`}>
                    <colgroup>
                        {columnWidths.map((style, index) => (
                            <col key={index} style={style} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            <th width="8%">Payment</th>
                            {hasPaymentDetails ? <th /> : null}
                            <th>Date Created</th>
                            <th>Created by</th>
                            <th>Product</th>
                            <th>Payment Type</th>
                            <th>Total</th>
                            <th>Paid</th>
                            <th>Balance</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {links.length === 0 ? (
                            <tr>
                                <td colSpan={tableColSpan} className="obd-stripe-table__empty">
                                    No payment link found for the selected contract
                                </td>
                            </tr>
                        ) : (
                            links.map((row, index) => {
                                const payments = Array.isArray(row.payments) ? row.payments : [];
                                const createdBy = displayCreatedByName(row.created_By);
                                return (
                                    <React.Fragment key={`${row.payment_Link || 'link'}-${index}`}>
                                        <tr>
                                            <td className="obd-stripe-table__link">
                                                {row.payment_Link ? (
                                                    <a
                                                        className="obd-stripe-table__pay"
                                                        href={row.payment_Link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title={row.payment_Link}
                                                    >
                                                        Click to pay
                                                    </a>
                                                ) : null}
                                            </td>
                                            {hasPaymentDetails ? (
                                                <td>
                                                    {payments.length > 0 ? (
                                                        <button
                                                            type="button"
                                                            className="obd-stripe-page__details"
                                                            onClick={() =>
                                                                setExpanded((prev) => ({ ...prev, [index]: !prev[index] }))
                                                            }
                                                        >
                                                            Details
                                                        </button>
                                                    ) : null}
                                                </td>
                                            ) : null}
                                            <td className="obd-stripe-table__nowrap">{formatLinkDate(row.date_Generated)}</td>
                                            <td
                                                className="obd-stripe-table__user"
                                                title={createdBy.full !== createdBy.label ? createdBy.full : undefined}
                                            >
                                                {createdBy.label}
                                            </td>
                                            <td>{row.opportunity_Product}</td>
                                            <td>{row.paymentType}</td>
                                            <td className="obd-stripe-table__num">{usd(row.total_Amount)}</td>
                                            <td className="obd-stripe-table__num">{usd(row.amount_Paid)}</td>
                                            <td className="obd-stripe-table__num">{usd(row.balance_Amount)}</td>
                                            <td>{row.link_Status}</td>
                                        </tr>
                                        {expanded[index] ? (
                                            <tr>
                                                <td colSpan={tableColSpan} className="obd-stripe-table__nested-cell">
                                                    <table className="obd-stripe-table obd-stripe-table--nested">
                                                        <thead>
                                                            <tr>
                                                                <th>Amount Paid</th>
                                                                <th>Payment Date</th>
                                                                <th>Payment Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {payments.map((payment, pIdx) => (
                                                                <tr key={`${index}-p-${pIdx}`}>
                                                                    <td>{usd(payment.paid_Amount)}</td>
                                                                    <td>{formatLinkDate(payment.payment_Date)}</td>
                                                                    <td>{payment.payment_Status}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            </div>

            <div className="obd-stripe-footer">
                {amountDetails.totalAmount ? (
                    <div className="obd-stripe-total">Total Contract Amount: {usd(amountDetails.totalAmount)}</div>
                ) : null}

                <div className="obd-stripe-footer__row">
                    {splits.length > 0 ? (
                        <div
                            className="obd-stripe-summary"
                            style={{
                                gridTemplateColumns: `repeat(${Math.min(splits.length, 5)}, minmax(140px, 1fr))`,
                            }}
                        >
                            {splits.map((product) => (
                                <article key={product.product} className="obd-stripe-summary-card">
                                    <h3 className="obd-stripe-summary-card__title">{product.product}</h3>
                                    <dl className="obd-stripe-summary-card__rows">
                                        <div>
                                            <dt>Amount</dt>
                                            <dd>{usd(product.Contract_Amount)}</dd>
                                        </div>
                                        <div>
                                            <dt>Invoices</dt>
                                            <dd>{usd(product.invoices)}</dd>
                                        </div>
                                        <div>
                                            <dt>Payments</dt>
                                            <dd className="obd-stripe-summary__paid">{usd(product.payments)}</dd>
                                        </div>
                                        <div className="obd-stripe-summary-card__remain">
                                            <dt>Remaining</dt>
                                            <dd className="obd-stripe-summary__remain">{usd(product.balanceRemaining)}</dd>
                                        </div>
                                    </dl>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div />
                    )}
                    <div className="obd-stripe-footer__actions">
                        <button
                            type="button"
                            className="obd-stripe-page__btn"
                            onClick={() => void load()}
                        >
                            Check for Updates
                        </button>
                        <button
                            type="button"
                            className="obd-stripe-page__btn"
                            onClick={() => {
                                if (!pageInput) {
                                    setPageInput(
                                        buildStripePageInput(opportunityDetails, salesForceId, []),
                                    );
                                }
                                setNewOpen(true);
                            }}
                        >
                            New Payment
                        </button>
                    </div>
                </div>
            </div>

            {newOpen && pageInput ? (
                <StripeNewPaymentModal
                    open={newOpen}
                    data={pageInput}
                    onClose={() => setNewOpen(false)}
                    onInPersonLink={(link) => {
                        setNewOpen(false);
                        window.open(link, '_blank', 'noopener,noreferrer');
                        void load();
                    }}
                    onStatus={(next) => {
                        setNewOpen(false);
                        setStatus(next);
                        void load();
                    }}
                />
            ) : null}

            <DashboardModal
                open={!!status}
                onClose={() => setStatus(null)}
                title={status?.title || 'Credit Card Payment'}
                width={480}
                theme={theme}
                footer={null}
            >
                {status ? (
                    <div className="obd-stripe-status">
                        <p>{status.message}</p>
                        {status.link ? (
                            <p>
                                {status.messageLink}{' '}
                                <a href={status.link} target="_blank" rel="noreferrer">
                                    {status.link}
                                </a>
                            </p>
                        ) : (
                            <p>{status.messageLink}</p>
                        )}
                    </div>
                ) : null}
            </DashboardModal>
                    </div>
                </div>
            </div>
        </DashboardModal>
    );
};

type StripeNewPaymentModalProps = {
    open: boolean;
    data: StripePageInput;
    onClose: () => void;
    onInPersonLink: (link: string) => void;
    onStatus: (status: StatusState) => void;
};

const StripeNewPaymentModal: React.FC<StripeNewPaymentModalProps> = ({ open, data, onClose, onInPersonLink, onStatus }) => {
    const theme = useOneButtonStore((s) => s.theme);
    const [form] = Form.useForm<NewPaymentForm>();
    const [types, setTypes] = useState<StripePaymentType[]>([]);
    const [loading, setLoading] = useState(false);
    const [validAmount, setValidAmount] = useState(true);

    useEffect(() => {
        if (!open) return;
        setValidAmount(true);
        form.setFieldsValue({
            // `undefined`, not `''` — an empty string counts as a selected value and hides the placeholder.
            customerPreference: undefined,
            productType: data.productType || undefined,
            PaymentType: undefined,
            description: '',
            amount: null,
            address: data.customerAddress,
            email: data.customerEmail,
            phoneNumber: formatPhoneMask(data.customerPhone || ''),
        });
        setLoading(true);
        stripeServices
            .getStripePaymentType()
            .then((rows) => setTypes(rows))
            .catch(() => setTypes([]))
            .finally(() => setLoading(false));
    }, [data, form, open]);

    const checkAmount = (amount: number | null | undefined) => {
        if (data.totalEpc && data.totalAmount && data.totalEpc > 0 && Number(amount || 0) > data.totalEpc - data.totalAmount) {
            setValidAmount(false);
        } else {
            setValidAmount(true);
        }
    };

    const getContractId = (): number => {
        const productType = String(form.getFieldValue('productType') || '');
        const match = data.StripeContractAmount?.find((ele) => ele.ProductName === productType);
        return match?.OpportunityContractId ?? 0;
    };

    const savePayment = async () => {
        try {
            const values = await form.validateFields();
            if (!validAmount || !values.amount) return;
            setLoading(true);
            const contractId = getContractId();
            const payload = {
                application_Id: data.application_Id,
                contract_Id: contractId,
                opportunity_Id: data.opportunityId,
                customerPreference: values.customerPreference,
                product_Name: `${String(data.salesForceId)} - ${data.Contact_Last_Name}, ${data.Contact_First_Name} - ${String(values.productType)} - ${String(values.PaymentType)}`,
                opportunity_Product: String(values.productType),
                PaymentType: String(values.PaymentType),
                payment_Description: values.description,
                total_Amount: values.amount,
                address: values.address,
                payment_Link_Email: values.email,
                payment_Link: '',
                phone: values.phoneNumber,
                date_Generated: null,
                created_By: '',
                TotalContractAmount: data.totalEpc,
                salesforce_Opportunity_Id: data.salesforce_Opportunity_Id,
                customer_Name: data.customer_Name,
                customer_Address: data.customerAddress,
                customer_Phone: data.customerPhone,
                salesperson_Name: data.salesperson_Name,
                salesperson_Phone: data.salesperson_Phone,
                salesperson_Email: data.salesperson_Email,
            };
            const link = await stripeServices.createNewPayment(payload);
            if (link && values.customerPreference === 'In Person') {
                onInPersonLink(link);
            } else if (link && values.customerPreference === 'Email') {
                onStatus({
                    title: 'Credit Card Payment - Email',
                    link: '',
                    message: 'Payment link is successfully generated',
                    messageLink: `Payment link has been sent to ${values.email}`,
                    status: 'success',
                });
            } else {
                onStatus({
                    title: 'Credit Card Payment',
                    link: String(link || ''),
                    message: 'Payment Link Generation Failed!',
                    messageLink: 'Please try again or check your internet connection.',
                    status: 'failed',
                });
            }
        } catch (error: unknown) {
            if (error && typeof error === 'object' && 'errorFields' in (error as object)) return;
            onStatus({
                title: 'Credit Card Payment',
                link: '',
                message: 'Payment Link Generation Failed!',
                messageLink: 'Please try again or check your internet connection.',
                status: 'failed',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title="Credit Card Payment"
            width={600}
            theme={theme}
            className={`obd-stripe-new-payment-modal obd-theme-${theme}`}
            footer={
                <div className="obd-stripe-form__footer">
                    <button type="button" className="obd-stripe-page__btn obd-stripe-page__btn--ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="obd-stripe-page__btn obd-stripe-page__btn--primary"
                        disabled={loading}
                        onClick={() => void savePayment()}
                    >
                        New Payment
                    </button>
                </div>
            }
        >
            {loading ? (
                <div className="obd-stripe-page__loader">
                    <Spin />
                </div>
            ) : null}
            <Form form={form} layout="vertical" className="obd-stripe-form" requiredMark={false}>
                <p className="obd-stripe-form__section">Payment</p>
                <Form.Item
                    className="obd-stripe-form__half"
                    label="Customer Preference"
                    name="customerPreference"
                    rules={[{ required: true, message: 'Customer Preference is required' }]}
                >
                    <OBDSelect
                        placeholder="Choose"
                        options={[
                            { value: 'In Person', label: 'In Person' },
                            { value: 'Email', label: 'Email' },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    className="obd-stripe-form__half"
                    label="Payment Type"
                    name="PaymentType"
                    rules={[{ required: true, message: 'Payment Type is required' }]}
                >
                    <OBDSelect
                        placeholder="Choose"
                        options={types.map((option) => ({
                            value: paymentTypeName(option),
                            label: paymentTypeName(option),
                        }))}
                    />
                </Form.Item>
                <Form.Item className="obd-stripe-form__full" label="Product" required>
                    <div className="obd-stripe-product">
                        <span className="obd-stripe-product__prefix">
                            {data.salesForceId || data.salesforce_Opportunity_Id}
                        </span>
                        <Form.Item name="productType" noStyle rules={[{ required: true }]}>
                            <OBDSelect
                                placeholder="Choose"
                                options={data.productTypeList.map((option) => ({ value: option, label: option }))}
                            />
                        </Form.Item>
                    </div>
                </Form.Item>
                <Form.Item
                    className="obd-stripe-form__full"
                    label="Description"
                    name="description"
                    rules={[{ required: true, message: 'Description is required' }, { max: 128 }]}
                >
                    <Input maxLength={128} placeholder="What is this payment for?" />
                </Form.Item>
                <Form.Item
                    className="obd-stripe-form__half"
                    label="Amount"
                    name="amount"
                    rules={[
                        {
                            validator: async (_, value) => {
                                if (value == null || value === '' || Number(value) === 0) {
                                    return Promise.reject(new Error('Amount is required'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                    extra={!validAmount ? 'Amount should be less than the total EPC value' : undefined}
                    validateStatus={!validAmount ? 'error' : undefined}
                >
                    <InputNumber
                        className="obd-stripe-amount"
                        prefix="$"
                        min={0}
                        precision={2}
                        placeholder="0.00"
                        controls={false}
                        onChange={(value) => checkAmount(typeof value === 'number' ? value : null)}
                    />
                </Form.Item>
                <Form.Item
                    className="obd-stripe-form__half"
                    label="Customer Phone"
                    name="phoneNumber"
                    rules={[
                        { required: true, message: 'Valid phone number is required' },
                        { min: 14, message: 'Valid phone number is required' },
                        { max: 14, message: 'Valid phone number is required' },
                    ]}
                >
                    <Input
                        maxLength={14}
                        placeholder="(000) 000 0000"
                        onChange={(event) => {
                            form.setFieldValue('phoneNumber', formatPhoneMask(event.target.value));
                        }}
                    />
                </Form.Item>
                <p className="obd-stripe-form__section">Customer</p>
                <Form.Item
                    className="obd-stripe-form__full"
                    label="Customer Email"
                    name="email"
                    rules={[
                        { required: true, message: 'Valid Email is required' },
                        { type: 'email', message: 'Valid Email is required' },
                    ]}
                >
                    <Input placeholder="name@example.com" />
                </Form.Item>
                <Form.Item
                    className="obd-stripe-form__full"
                    label="Customer Address"
                    name="address"
                    rules={[{ required: true, message: 'Address is required' }, { min: 6 }]}
                >
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                </Form.Item>
            </Form>
        </DashboardModal>
    );
};

export type StripePaymentModalProps = {
    open: boolean;
    onClose: () => void;
    opportunityNumericId: string | number | null | undefined;
    salesForceId: string | null | undefined;
};

const StripePaymentModal: React.FC<StripePaymentModalProps> = ({
    open,
    onClose,
    opportunityNumericId,
    salesForceId,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const financePartnersList = useOneButtonStore((s) => s.financePartnersList);
    const blockMessage = open
        ? stripeFinancePartnerSyncMessage(opportunityDetails, financePartnersList)
        : null;
    const modalOpen = open && !blockMessage;

    useEffect(() => {
        if (!open || !blockMessage) return;
        showStripeFinancePartnerSyncModal(blockMessage, theme);
        onClose();
    }, [open, blockMessage, onClose, theme]);

    return modalOpen ? (
        <StripePaymentPanel
            open={modalOpen}
            onClose={onClose}
            opportunityNumericId={opportunityNumericId}
            salesForceId={salesForceId}
        />
    ) : null;
};

export default StripePaymentModal;
